import { useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState, forwardRef } from 'react'
import { X, XCircle, ZoomIn, ZoomOut, Loader2 } from 'lucide-react'
import * as pdfjsLib from 'pdfjs-dist'
import { API_BASE_URL, getApiHeaders } from '@/api/client'
import { ensureFreshToken } from '@/api/tokenStore'

import { drawHighlightOnCanvas, drawHighlightContinuation, clearHighlightCanvas, locateInStripped } from './highlightPdfQuote'

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdfjs/pdf.worker.min.mjs'

const ZOOM_MIN = 0.5
const ZOOM_MAX = 3.0
const ZOOM_STEP = 0.25
const SIDE_PADDING = 24
const QUALITY = 2  // canvas rendered at 2x for sharpness; CSS display size is half canvas pixels

export interface ViewerHandle {
  scrollToCitation: () => void
}

export interface DocTab {
  id: string
  title: string
  legislationUuid: string
  citedText: string
  positionFraction: number | null  // 0–1 hint for which occurrence to highlight
  isPdf: boolean                    // true = PDF viewer, false = regulation markdown viewer
}

interface DocumentPanelProps {
  tabs: DocTab[]
  activeTabId: string | null
  onTabSelect: (id: string) => void
  onTabClose: (id: string) => void
  onCloseAll: () => void
  onResizeStart: (e: React.MouseEvent) => void
  width: number
}

const DocumentPanel = forwardRef<ViewerHandle, DocumentPanelProps>(function DocumentPanel({
  tabs,
  activeTabId,
  onTabSelect,
  onTabClose,
  onCloseAll,
  onResizeStart,
  width,
}, ref) {
  const activeTab = tabs.find((t) => t.id === activeTabId) ?? null
  const activeViewerRef = useRef<ViewerHandle | null>(null)

  useImperativeHandle(ref, () => ({
    scrollToCitation: () => activeViewerRef.current?.scrollToCitation(),
  }), [])

  return (
    <div
      className="relative flex flex-col h-full bg-white border-l border-gray-200 shadow-[-4px_0_12px_rgba(0,0,0,0.04)]"
      style={{ width }}
    >
      {/* Drag handle — overlays the left border, invisible at rest, brand blue on hover */}
      <div
        onMouseDown={onResizeStart}
        className="absolute left-[-2px] top-0 w-1 h-full z-10 cursor-col-resize rounded-sm transition-colors hover:bg-brand"
        title="Drag to resize"
      />
      {/* Tab strip */}
      <div className="flex items-end gap-1 px-2 pt-2 bg-gray-100 flex-shrink-0">
        <div className="flex items-end gap-1 flex-1 min-w-0 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => onTabSelect(tab.id)}
              className={`flex items-center gap-1.5 pl-3 pr-1.5 h-8 min-w-0 max-w-[220px] rounded-t-lg text-xs flex-shrink-0 transition-colors ${
                tab.id === activeTabId
                  ? 'bg-white text-gray-800 font-medium'
                  : 'bg-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700'
              }`}
            >
              <span className="truncate flex-1 min-w-0 text-left">{tab.title}</span>
              <span
                role="button"
                onClick={(e) => { e.stopPropagation(); onTabClose(tab.id) }}
                className="flex-shrink-0 p-0.5 rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-600"
              >
                <X className="w-2.5 h-2.5" />
              </span>
            </button>
          ))}
        </div>
        <button onClick={onCloseAll} title="Close all"
          className="mb-0.5 p-1.5 rounded text-gray-400 hover:bg-gray-200 hover:text-gray-600 flex-shrink-0">
          <XCircle className="w-4 h-4" />
        </button>
      </div>

      {/* Render all viewers, show only active — no unmount on tab switch */}
      <div className="flex-1 min-h-0 relative">
        {tabs.map((tab) => (
          <div
            key={tab.legislationUuid}
            style={{
              display: tab.id === activeTabId ? 'flex' : 'none',
              position: 'absolute',
              inset: 0,
              flexDirection: 'column',
            }}
          >
            {tab.isPdf
              ? <PdfViewer ref={tab.id === activeTabId ? activeViewerRef : null} tab={tab} panelWidth={width} isActive={tab.id === activeTabId} />
              : <RegulationViewer ref={tab.id === activeTabId ? activeViewerRef : null} tab={tab} isActive={tab.id === activeTabId} />
            }
          </div>
        ))}
        {!activeTab && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-400">
            No document selected
          </div>
        )}
      </div>
    </div>
  )
})

export default DocumentPanel

// ---------------------------------------------------------------------------

type RenderedPage = {
  page: pdfjsLib.PDFPageProxy
  viewport: pdfjsLib.PageViewport
  wrapper: HTMLDivElement
  canvas: HTMLCanvasElement
  highlightCanvas: HTMLCanvasElement
}

const LAZY_INITIAL = 5    // pages to render around the target on first load
const LAZY_OBSERVE = 400  // IntersectionObserver rootMargin (%) — pre-render pages 4 viewports ahead
const MAX_RENDERED = 20   // evict farthest pages once rendered count exceeds this

const PdfViewer = forwardRef<ViewerHandle, { tab: DocTab; panelWidth: number; isActive: boolean }>(function PdfViewer({ tab, panelWidth, isActive }, ref) {
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const pdfDocRef = useRef<pdfjsLib.PDFDocumentProxy | null>(null)
  const renderedPagesRef = useRef<(RenderedPage | null)[]>([])
  const pageWrappersRef = useRef<HTMLDivElement[]>([])   // wrappers for ALL pages (rendered or placeholder)
  const renderedSetRef = useRef<Set<number>>(new Set())   // which page numbers have been rendered
  const observerRef = useRef<IntersectionObserver | null>(null)
  const scaleRef = useRef(1)
  const zoomRef = useRef(1.0)
  const currentPageRef = useRef(1)
  const citedTextRef = useRef(tab.citedText)
  const positionFractionRef = useRef(tab.positionFraction)
  const pendingScrollPageRef = useRef<number | null>(null)
  const protectedRangeRef = useRef<[number, number] | null>(null)  // pages around highlight — never evict
  const uuidRef = useRef(tab.legislationUuid)
  const loadingTaskRef = useRef<pdfjsLib.PDFDocumentLoadingTask | null>(null)

  const [zoom, setZoom] = useState(1.0)
  const [numPages, setNumPages] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    citedTextRef.current = tab.citedText
    positionFractionRef.current = tab.positionFraction
  }, [tab.citedText, tab.positionFraction])

  // ---------- highlight helpers ----------

  const applyHighlights = useCallback(async (
    citedText: string,
    positionFraction: number | null,
  ): Promise<number | null> => {
    for (const p of renderedPagesRef.current) if (p) clearHighlightCanvas(p.highlightCanvas)

    const totalPages = renderedPagesRef.current.length
    if (totalPages === 0) return null

    // Highlight a specific page index, plus continuation on next page if cross-page
    const highlightPage = async (idx: number): Promise<number | null> => {
      const entry = renderedPagesRef.current[idx]
      if (!entry) return null  // page not rendered yet — skip
      const { firstY, crossPage } = await drawHighlightOnCanvas(
        entry.page, entry.viewport, entry.highlightCanvas, citedText,
      )
      if (crossPage && idx + 1 < totalPages) {
        const next = renderedPagesRef.current[idx + 1]
        if (next) await drawHighlightContinuation(next.page, next.viewport, next.highlightCanvas, citedText)
      }
      return firstY
    }

    // Spiral outward from the estimated target page — checks the most likely
    // page first and expands outward.  Only checks rendered pages (skips nulls),
    // never calls getPage/findAllCitationPages so no extra network requests.
    const startIdx = positionFraction !== null
      ? Math.max(0, Math.min(totalPages - 1, Math.round(positionFraction * (totalPages - 1))))
      : 0

    for (let offset = 0; offset < totalPages; offset++) {
      const indices = offset === 0 ? [startIdx] : [startIdx - offset, startIdx + offset]
      for (const idx of indices) {
        if (idx < 0 || idx >= totalPages) continue
        const firstY = await highlightPage(idx)
        if (firstY !== null) return idx + 1
      }
    }
    return null
  }, [])

  const scrollToHighlight = useCallback((pageNum: number) => {
    const entry = renderedPagesRef.current[pageNum - 1]
    const scrollEl = scrollContainerRef.current
    if (!scrollEl) return
    // If page is rendered, highlight and scroll to the exact citation position;
    // otherwise fall back to scrolling the placeholder wrapper into view.
    if (entry) {
      drawHighlightOnCanvas(entry.page, entry.viewport, entry.highlightCanvas, citedTextRef.current)
        .then(({ firstY }) => {
          // Wait one frame for the browser to lay out newly rendered pages
          // so offsetTop values are accurate before computing scroll position
          requestAnimationFrame(() => {
            const cssZoom = zoomRef.current / renderedZoomRef.current
            const targetTop = firstY !== null
              ? entry.wrapper.offsetTop + (firstY / QUALITY) * cssZoom - scrollEl.clientHeight / 2
              : entry.wrapper.offsetTop
            scrollEl.scrollTo({ top: Math.max(0, targetTop), behavior: 'instant' as ScrollBehavior })
          })
        })
    } else {
      const wrapper = pageWrappersRef.current[pageNum - 1]
      if (wrapper) {
        requestAnimationFrame(() => {
          scrollEl.scrollTo({ top: wrapper.offsetTop, behavior: 'instant' as ScrollBehavior })
        })
      }
    }
  }, [])

  // ---------- render a single page to its placeholder wrapper ----------

  const renderSinglePage = useCallback(async (pageNum: number) => {
    const doc = pdfDocRef.current
    if (!doc || renderedSetRef.current.has(pageNum)) return
    renderedSetRef.current.add(pageNum)

    const scale = scaleRef.current
    const page = await doc.getPage(pageNum)
    const viewport = page.getViewport({ scale })
    const cssW = viewport.width / QUALITY
    const cssH = viewport.height / QUALITY

    const wrapper = pageWrappersRef.current[pageNum - 1]
    if (!wrapper) return

    // Update wrapper to match actual page dimensions (may differ from page-1 estimate)
    wrapper.style.width = `${cssW}px`
    wrapper.style.height = `${cssH}px`
    wrapper.style.background = ''

    const canvas = document.createElement('canvas')
    canvas.width = viewport.width
    canvas.height = viewport.height
    canvas.style.cssText = `display:block;width:${cssW}px;height:${cssH}px`
    wrapper.appendChild(canvas)

    const highlightCanvas = document.createElement('canvas')
    highlightCanvas.width = viewport.width
    highlightCanvas.height = viewport.height
    highlightCanvas.style.cssText = `position:absolute;top:0;left:0;pointer-events:none;width:${cssW}px;height:${cssH}px`
    wrapper.appendChild(highlightCanvas)

    const ctx = canvas.getContext('2d')
    if (ctx) {
      try {
        await page.render({ canvasContext: ctx, viewport, canvas }).promise
      } catch (e: unknown) {
        if ((e as { name?: string })?.name !== 'RenderingCancelledException') {
          console.error('PDF render error', e)
        }
      }
    }

    try {
      const textLayerDiv = document.createElement('div')
      textLayerDiv.className = 'pdf-text-layer'
      textLayerDiv.style.cssText = `position:absolute;left:0;top:0;width:${cssW}px;height:${cssH}px`
      textLayerDiv.style.setProperty('--scale-factor', String(scale / QUALITY))
      wrapper.appendChild(textLayerDiv)
      const textContent = await page.getTextContent()
      const textLayer = new pdfjsLib.TextLayer({ textContentSource: textContent, container: textLayerDiv, viewport })
      await textLayer.render()
    } catch { /* text layer is best-effort */ }

    renderedPagesRef.current[pageNum - 1] = { page, viewport, wrapper, canvas, highlightCanvas }
    console.debug(`[pdf-render] rendered page ${pageNum}, total rendered=${renderedSetRef.current.size}`)
  }, [])

  // ---------- evict a rendered page back to a placeholder ----------

  const evictPage = useCallback((pageNum: number) => {
    // Never evict pages in the protected range around the active highlight
    const pr = protectedRangeRef.current
    if (pr && pageNum >= pr[0] && pageNum <= pr[1]) return

    const entry = renderedPagesRef.current[pageNum - 1]
    const wrapper = pageWrappersRef.current[pageNum - 1]
    if (!entry || !wrapper) return

    // Tear down canvas + text layer DOM, restore placeholder background
    wrapper.innerHTML = ''
    wrapper.style.background = '#fff'

    renderedPagesRef.current[pageNum - 1] = null
    renderedSetRef.current.delete(pageNum)

    // Re-observe so the IntersectionObserver can re-render if scrolled back
    observerRef.current?.observe(wrapper)
    console.debug(`[pdf-evict] evicted page ${pageNum}, rendered=${renderedSetRef.current.size} pages=[${[...renderedSetRef.current].sort((a,b)=>a-b).join(',')}]`)
  }, [])

  // After rendering a page, evict the farthest pages if over the limit
  const evictIfNeeded = useCallback((anchorPage: number) => {
    if (renderedSetRef.current.size <= MAX_RENDERED) return
    console.debug(`[pdf-evict] evicting: anchor=${anchorPage} rendered=${renderedSetRef.current.size} max=${MAX_RENDERED}`)
    // Sort rendered pages by distance from anchor, evict the farthest
    const sorted = [...renderedSetRef.current].sort(
      (a, b) => Math.abs(b - anchorPage) - Math.abs(a - anchorPage),
    )
    while (sorted.length > MAX_RENDERED) {
      evictPage(sorted.shift()!)
    }
  }, [evictPage])

  // ---------- render pages around a target, expand until highlight found ----------

  const renderAndHighlight = useCallback(async (
    citedText: string,
    positionFraction: number | null,
  ): Promise<number | null> => {
    const doc = pdfDocRef.current
    if (!doc || citedText.length === 0) return null

    const totalPages = doc.numPages
    const targetPage = positionFraction !== null
      ? Math.max(1, Math.min(totalPages, Math.round(positionFraction * (totalPages - 1)) + 1))
      : 1

    let exStart = Math.max(1, targetPage - LAZY_INITIAL)
    let exEnd = Math.min(totalPages, targetPage + LAZY_INITIAL)

    // Render initial window (idempotent — already-rendered pages are no-ops)
    for (let i = exStart; i <= exEnd; i++) await renderSinglePage(i)

    let hitPage = await applyHighlights(citedText, positionFraction)

    // Expand outward until found or all pages rendered
    while (hitPage === null && (exStart > 1 || exEnd < totalPages)) {
      const newStart = Math.max(1, exStart - LAZY_INITIAL)
      const newEnd = Math.min(totalPages, exEnd + LAZY_INITIAL)
      for (let i = newStart; i < exStart; i++) await renderSinglePage(i)
      for (let i = exEnd + 1; i <= newEnd; i++) await renderSinglePage(i)
      exStart = newStart
      exEnd = newEnd
      hitPage = await applyHighlights(citedText, positionFraction)
    }

    // Protect ±LAZY_INITIAL pages around the highlight from eviction
    if (hitPage) {
      const totalPages = pdfDocRef.current?.numPages ?? hitPage
      protectedRangeRef.current = [
        Math.max(1, hitPage - LAZY_INITIAL),
        Math.min(totalPages, hitPage + LAZY_INITIAL),
      ]
      evictIfNeeded(hitPage)
    } else {
      protectedRangeRef.current = null
    }

    return hitPage
  }, [renderSinglePage, applyHighlights, evictIfNeeded])

  useImperativeHandle(ref, () => ({
    scrollToCitation: () => {
      if (!pdfDocRef.current || !citedTextRef.current) return
      renderAndHighlight(citedTextRef.current, positionFractionRef.current).then((hitPage) => {
        scrollToHighlight(hitPage ?? currentPageRef.current)
      })
    },
  }), [renderAndHighlight, scrollToHighlight])

  // ---------- render ----------

  const renderPDF = useCallback(async (doc: pdfjsLib.PDFDocumentProxy, citedText: string, isNewDoc = false) => {
    if (!containerRef.current || !scrollContainerRef.current) return

    // Tear down previous observer + state
    observerRef.current?.disconnect()
    observerRef.current = null
    pdfjsLib.TextLayer.cleanup()
    containerRef.current.innerHTML = ''
    renderedPagesRef.current = new Array(doc.numPages).fill(null)
    pageWrappersRef.current = []
    renderedSetRef.current = new Set()

    const panelW = scrollContainerRef.current.clientWidth || panelWidth - SIDE_PADDING
    const firstPage = await doc.getPage(1)
    const naturalWidth = firstPage.getViewport({ scale: 1 }).width
    if (isNewDoc) {
      zoomRef.current = 1.0
      renderedZoomRef.current = 1.0
      setZoom(1.0)
    }
    const baseScale = Math.max(0.5, (panelW - SIDE_PADDING) / naturalWidth)
    const scale = baseScale * zoomRef.current * QUALITY
    scaleRef.current = scale
    renderedZoomRef.current = zoomRef.current

    // Clear any CSS zoom — pages are now rendered at true scale
    if (containerRef.current) containerRef.current.style.zoom = ''

    setVisible(false)
    setNumPages(doc.numPages)
    setCurrentPage(1)
    currentPageRef.current = 1

    // --- Phase 1: Create lightweight placeholder wrappers for ALL pages ---
    const defaultVp = firstPage.getViewport({ scale })
    const cssW = defaultVp.width / QUALITY
    const cssH = defaultVp.height / QUALITY

    for (let i = 1; i <= doc.numPages; i++) {
      const wrapper = document.createElement('div')
      wrapper.style.cssText = `position:relative;margin:0 auto 8px;width:${cssW}px;height:${cssH}px;background:#fff`
      wrapper.className = 'shadow-md'
      wrapper.dataset.pageNum = String(i)
      containerRef.current.appendChild(wrapper)
      pageWrappersRef.current.push(wrapper)
    }

    // --- Phase 2: Determine target page & render initial window ---
    let targetPage = 1
    if (citedText.length > 0 && positionFractionRef.current !== null) {
      // Use positionFraction to estimate citation page instantly (no network scan)
      targetPage = Math.max(1, Math.min(doc.numPages,
        Math.round(positionFractionRef.current * (doc.numPages - 1)) + 1))
    }

    const renderStart = Math.max(1, targetPage - LAZY_INITIAL)
    const renderEnd = Math.min(doc.numPages, targetPage + LAZY_INITIAL)
    for (let i = renderStart; i <= renderEnd; i++) {
      await renderSinglePage(i)
    }

    // --- Phase 3: Set up IntersectionObserver for remaining pages ---
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          const pn = parseInt((entry.target as HTMLElement).dataset.pageNum!)
          if (isNaN(pn) || renderedSetRef.current.has(pn)) continue
          observer.unobserve(entry.target)
          renderSinglePage(pn)
            .then(() => evictIfNeeded(pn))
            .catch(console.error)
        }
      },
      { root: scrollContainerRef.current, rootMargin: `${LAZY_OBSERVE}% 0px` },
    )
    observerRef.current = observer
    pageWrappersRef.current.forEach((w, i) => {
      if (!renderedSetRef.current.has(i + 1)) observer.observe(w)
    })

    // --- Phase 4: Highlights + scroll ---
    if (citedText.length > 0) {
      const hitPage = await renderAndHighlight(citedText, positionFractionRef.current)
      const scrollTarget = hitPage ?? targetPage
      if (isActive) {
        scrollToHighlight(scrollTarget)
      } else {
        pendingScrollPageRef.current = scrollTarget
      }
    }

    setVisible(true)
  }, [scrollToHighlight, panelWidth, renderSinglePage, evictIfNeeded, renderAndHighlight])

  // ---------- load PDF when UUID changes ----------

  useEffect(() => {
    let cancelled = false
    uuidRef.current = tab.legislationUuid
    setLoading(true)
    setError(null)
    setNumPages(0)
    setVisible(false)

    async function load() {
      await ensureFreshToken()
      const url = `${API_BASE_URL}/api/v1/documents/${tab.legislationUuid}/pdf`

      // Pass the URL directly to PDF.js so it can use HTTP range requests —
      // only the pages being rendered are fetched, never the whole file.
      const loadingTask = pdfjsLib.getDocument({
        url,
        httpHeaders: getApiHeaders(),
        rangeChunkSize: 524288,  // 512 KB chunks
        disableStream: true,     // prevent full-file download on first request; force range-only mode
        disableAutoFetch: true,  // only fetch page data on demand, not the entire file upfront
        standardFontDataUrl: '/pdfjs/standard_fonts/',
        cMapUrl: '/pdfjs/cmaps/',
        cMapPacked: true,
        wasmUrl: '/pdfjs/wasm/',
      })
      loadingTaskRef.current = loadingTask

      try {
        const doc = await loadingTask.promise
        if (cancelled) { loadingTask.destroy(); return }

        pdfDocRef.current = doc
        await renderPDF(doc, citedTextRef.current, true)
        if (!cancelled) setLoading(false)
      } catch (e: unknown) {
        if (cancelled) return
        const status = (e as { status?: number })?.status
        setError(
          status === 404 ? 'No PDF available for this document.' :
          status === 403 ? 'You do not have access to this document.' :
          e instanceof Error ? e.message : 'Failed to load PDF'
        )
        setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
      observerRef.current?.disconnect()
      observerRef.current = null
      loadingTaskRef.current?.destroy()
      loadingTaskRef.current = null
    }
  }, [tab.legislationUuid]) // eslint-disable-line react-hooks/exhaustive-deps

  // When this tab becomes active, apply any pending scroll that was deferred
  // because getBoundingClientRect returns zeros on display:none elements
  useEffect(() => {
    if (!isActive) return
    if (pendingScrollPageRef.current !== null) {
      const page = pendingScrollPageRef.current
      pendingScrollPageRef.current = null
      // Wait one frame for the browser to compute layout after display:none → flex
      requestAnimationFrame(() => scrollToHighlight(page))
    }
  }, [isActive, scrollToHighlight])

  // Re-highlight when citedText changes — may render new pages if needed
  const [searching, setSearching] = useState(false)
  useEffect(() => {
    if (!pdfDocRef.current || loading) return
    setSearching(true)
    renderAndHighlight(tab.citedText, positionFractionRef.current)
      .then((hitPage) => {
        if (hitPage) scrollToHighlight(hitPage)
      })
      .finally(() => setSearching(false))
  }, [tab.citedText, loading, renderAndHighlight, scrollToHighlight])

  // ---------- scroll tracking ----------

  useEffect(() => {
    const scrollEl = scrollContainerRef.current
    if (!scrollEl) return
    const handleScroll = () => {
      const wrappers = pageWrappersRef.current
      if (!wrappers.length) return
      const center = scrollEl.scrollTop + scrollEl.clientHeight / 2
      let closest = 0, closestDist = Infinity
      wrappers.forEach((w, i) => {
        const dist = Math.abs(w.offsetTop + w.clientHeight / 2 - center)
        if (dist < closestDist) { closestDist = dist; closest = i }
      })
      currentPageRef.current = closest + 1
      setCurrentPage(closest + 1)
    }
    scrollEl.addEventListener('scroll', handleScroll, { passive: true })
    return () => scrollEl.removeEventListener('scroll', handleScroll)
  }, [])

  // ---------- zoom (CSS scale only — no re-render) ----------

  // renderedZoomRef tracks the zoom level at which pages were last rendered,
  // so the CSS zoom ratio stays correct across multiple zoom steps.
  const renderedZoomRef = useRef(1.0)

  const doZoom = useCallback((next: number) => {
    if (next === zoomRef.current || !containerRef.current) return
    const scrollEl = scrollContainerRef.current

    // Save scroll fraction before zoom changes layout
    const scrollFraction = scrollEl
      ? scrollEl.scrollTop / Math.max(1, scrollEl.scrollHeight - scrollEl.clientHeight)
      : 0

    zoomRef.current = next
    setZoom(next)

    // CSS zoom — instant, no re-render, layout-aware (unlike transform:scale)
    const ratio = next / renderedZoomRef.current
    containerRef.current.style.zoom = ratio === 1 ? '' : String(ratio)

    // Restore relative scroll position after browser re-lays out at new zoom
    if (scrollEl) {
      requestAnimationFrame(() => {
        const newMax = scrollEl.scrollHeight - scrollEl.clientHeight
        scrollEl.scrollTop = scrollFraction * Math.max(0, newMax)
      })
    }
  }, [])

  useEffect(() => {
    const el = scrollContainerRef.current
    if (!el) return
    const handleWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return
      e.preventDefault()
      const delta = e.deltaMode === 0 ? e.deltaY / 300 : e.deltaY * 0.1
      const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN,
        Math.round(zoomRef.current * Math.exp(-delta) * 100) / 100))
      doZoom(next)
    }
    el.addEventListener('wheel', handleWheel, { passive: false })
    return () => el.removeEventListener('wheel', handleWheel)
  }, [doZoom])

  // ---------- render ----------

  return (
    <div className="flex flex-col flex-1 min-h-0 w-full h-full">
      {/* Citation quote header */}
      {tab.citedText && (
        <div className="px-3 py-2.5 flex-shrink-0">
          <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1.5">Cited text</div>
          <div className="bg-gray-50 border border-gray-200 rounded-xl px-2.5 py-2 text-[13px] leading-relaxed text-gray-600 overflow-y-auto max-h-24" style={{ fontFamily: 'Roboto, sans-serif' }}>
            {tab.citedText}
          </div>
        </div>
      )}

      {/* PDF area — rounded window with white padding around pages, matching mike OSS */}
      <div className="relative flex-1 min-h-0 px-3 pb-3 pt-1">
        <div className="relative h-full rounded-xl border border-gray-200 overflow-hidden">
          {(loading || searching) && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                {searching ? 'Finding citation…' : 'Loading document…'}
              </div>
            </div>
          )}
          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
              <p className="text-sm text-gray-500 text-center px-6">{error}</p>
            </div>
          )}

          <div
            ref={scrollContainerRef}
            className="absolute inset-0 overflow-auto bg-gray-100 px-3 pt-5 pb-3"
            style={{ opacity: visible ? 1 : 0 }}
          >
            <div ref={containerRef} />
          </div>

        {numPages > 0 && !loading && !error && (
          <>
            <div className="absolute bottom-4 left-4 pointer-events-none z-10">
              <span className="flex items-center px-3 py-1.5 rounded-full text-xs font-medium tabular-nums text-gray-700 bg-white/80 backdrop-blur border border-white/40 shadow">
                {currentPage} / {numPages}
              </span>
            </div>
            <div className="absolute bottom-4 right-4 flex items-center gap-px rounded-full bg-white/80 backdrop-blur border border-white/40 shadow px-1 py-1 z-10">
              <button onClick={handleZoomOut} disabled={zoom <= ZOOM_MIN}
                className="flex items-center justify-center w-7 h-7 rounded-full text-gray-600 hover:bg-white disabled:opacity-30 transition-colors">
                <ZoomOut className="w-3.5 h-3.5" />
              </button>
              <span className="text-xs font-medium text-gray-600 tabular-nums w-9 text-center select-none">
                {Math.round(zoom * 100)}%
              </span>
              <button onClick={handleZoomIn} disabled={zoom >= ZOOM_MAX}
                className="flex items-center justify-center w-7 h-7 rounded-full text-gray-600 hover:bg-white disabled:opacity-30 transition-colors">
                <ZoomIn className="w-3.5 h-3.5" />
              </button>
            </div>
          </>
        )}
        </div>{/* rounded window */}
      </div>
    </div>
  )

  function handleZoomIn() { doZoom(Math.min(ZOOM_MAX, Math.round((zoomRef.current + ZOOM_STEP) * 100) / 100)) }
  function handleZoomOut() { doZoom(Math.max(ZOOM_MIN, Math.round((zoomRef.current - ZOOM_STEP) * 100) / 100)) }
})

// ---------------------------------------------------------------------------
// RegulationViewer
// 1. Call /locate to find the exact page containing the cited text
// 2. Load [page-3 … page+3] in parallel (7 pages max)
// 3. Convert markdown→HTML directly (no ReactMarkdown / no shadow div)
// 4. Set displayRef.innerHTML, highlight, scrollIntoView
// 5. Lazy-load one page at a time as user scrolls near edges
// ---------------------------------------------------------------------------

interface PageData {
  page: number
  content: string
  total_pages: number
  title: string
  legal_ref: string
}

/** Convert the regulation markdown subset to HTML.
 *  Wraps output in a data-page div for DOM windowing. */
function mdToHtml(md: string, pageNum: number): string {
  const inner = md
    .split('\n')
    .map(line => {
      if (line.startsWith('### ')) return `<h3>${line.slice(4)}</h3>`
      if (line.startsWith('## '))  return `<h2>${line.slice(3)}</h2>`
      const html = line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      return html.trim() ? `<p>${html}</p>` : ''
    })
    .join('\n')
  return `<div data-page="${pageNum}">${inner}</div>`
}

const PAGE_WINDOW = 3    // pages to load before and after the citation page
const MAX_DOM_PAGES = 15 // evict from opposite end once DOM exceeds this

const RegulationViewer = forwardRef<ViewerHandle, { tab: DocTab; isActive: boolean }>(function RegulationViewer({ tab, isActive }, ref) {
  const [meta, setMeta] = useState<{ title: string; legal_ref: string } | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingTop, setLoadingTop] = useState(false)
  const [loadingBottom, setLoadingBottom] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [stickySection, setStickySection] = useState<string | null>(null)
  const [titleBarHeight, setTitleBarHeight] = useState(0)

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const titleBarRef = useRef<HTMLDivElement>(null)
  const displayRef = useRef<HTMLDivElement>(null)

  const isActiveRef = useRef(isActive)
  useEffect(() => { isActiveRef.current = isActive }, [isActive])

  const loadedRangeRef = useRef({ start: 0, end: 0 })
  const totalPagesRef = useRef(0)
  const loadingEdgeRef = useRef({ top: false, bottom: false })
  const pendingScrollRef = useRef(false)
  const pendingScrollTargetRef = useRef<number | undefined>(undefined)
  // In-memory cache: pageNum → rendered HTML string. Never evicted — ~5KB/page,
  // 200 pages ≈ 1MB. Avoids re-fetching pages the user has already scrolled past.
  const pageCacheRef = useRef<Map<number, string>>(new Map())

  const uuid = tab.legislationUuid

  // --- Helpers ---
  const headers = useCallback((): HeadersInit => getApiHeaders(), [])

  const fetchPage = useCallback(async (page: number): Promise<PageData> => {
    const res = await fetch(`${API_BASE_URL}/api/v1/documents/${uuid}/view?page=${page}`, { headers: headers() })
    if (!res.ok) throw new Error(res.status === 404 ? 'Document view not found.' : `Error loading page (${res.status})`)
    return res.json()
  }, [uuid, headers])

  // Returns HTML for a page — from cache if available, otherwise fetches and caches.
  const getPageHtml = useCallback(async (page: number): Promise<string> => {
    if (pageCacheRef.current.has(page)) return pageCacheRef.current.get(page)!
    const data = await fetchPage(page)
    const html = mdToHtml(data.content, page)
    pageCacheRef.current.set(page, html)
    return html
  }, [fetchPage])

  const applyAndScroll = useCallback((citedText: string, targetPage?: number) => {
    if (!displayRef.current) return
    highlightRegulationText(displayRef.current, citedText)
    // Prefer the highlight on the locate target page — avoids scrolling to a false
    // positive match on an earlier page when the same text fragment appears multiple times.
    let mark: HTMLElement | null = null
    if (targetPage) {
      const pageEl = displayRef.current.querySelector<HTMLElement>(`[data-page="${targetPage}"]`)
      mark = pageEl?.querySelector('.reg-highlight') ?? null
    }
    if (!mark) mark = displayRef.current.querySelector<HTMLElement>('.reg-highlight')
    if (mark) {
      mark.scrollIntoView({ behavior: 'instant' as ScrollBehavior, block: 'center' })
      setNotice(null) // found — clear any previous notice
    } else {
      setNotice('This citation could not be located in the current version of the document.')
    }
  }, [])

  useImperativeHandle(ref, () => ({
    scrollToCitation: () => applyAndScroll(tab.citedText),
  }), [applyAndScroll, tab.citedText])

  // --- Initial load ---
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setNotice(null)
    setMeta(null)
    setStickySection(null)
    loadedRangeRef.current = { start: 0, end: 0 }
    pageCacheRef.current = new Map() // new document — clear cache
    if (displayRef.current) displayRef.current.innerHTML = ''

    async function load() {
      await ensureFreshToken()
      try {
        let target = 1
        let total: number

        if (tab.citedText) {
          // Step 1: locate the exact page — POST so cited_text isn't in URL (WAF safe)
          const locRes = await fetch(
            `${API_BASE_URL}/api/v1/documents/${uuid}/view/locate`,
            {
              method: 'POST',
              headers: { ...headers(), 'Content-Type': 'application/json' },
              body: JSON.stringify({
                cited_text: tab.citedText,
                position_fraction: tab.positionFraction,
              }),
            },
          )
          if (!locRes.ok) {
            if (locRes.status === 404) throw new Error('Document view is not available at this time.')
            throw new Error('Failed to reach document service.')
          }
          const { page: citedPage, total_pages } = await locRes.json()
          if (cancelled) return
          target = citedPage ?? 1
          total = total_pages
        } else {
          // No citation — open at page 1, just need total_pages
          const firstPage = await fetchPage(1)
          if (cancelled) return
          total = firstPage.total_pages
          pageCacheRef.current.set(1, mdToHtml(firstPage.content, 1))
        }

        // Step 2: fetch window [target-PAGE_WINDOW … target+PAGE_WINDOW]
        const startPage = Math.max(1, target - PAGE_WINDOW)
        const endPage = Math.min(total, target + PAGE_WINDOW)
        const pageNums = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i)
        // Use cache for pages already fetched (e.g. page 1 in the no-citation path)
        const pages = await Promise.all(pageNums.map(n =>
          pageCacheRef.current.has(n) ? null : fetchPage(n)
        ))
        if (cancelled) return

        // Populate cache for newly fetched pages
        pages.forEach((p) => {
          if (p) pageCacheRef.current.set(p.page, mdToHtml(p.content, p.page))
        })

        // Get meta from first available page
        const firstReal = pages.find(p => p !== null)
        const metaSource = firstReal
          ? { title: firstReal.title, legal_ref: firstReal.legal_ref }
          : await fetchPage(target).then(p => ({ title: p.title, legal_ref: p.legal_ref }))

        totalPagesRef.current = total
        loadedRangeRef.current = { start: startPage, end: endPage }
        setMeta(metaSource)

        // Step 3: render from cache
        if (displayRef.current) {
          displayRef.current.innerHTML = pageNums.map(n => pageCacheRef.current.get(n)!).join('')
        }

        setLoading(false)

        // Step 4: highlight + scroll (skip if no citation)
        if (tab.citedText) {
          if (isActiveRef.current) {
            requestAnimationFrame(() => applyAndScroll(tab.citedText, target))
          } else {
            pendingScrollRef.current = true
            pendingScrollTargetRef.current = target
          }
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load document')
          setLoading(false)
        }
      }
    }

    load()
    return () => { cancelled = true }
  }, [uuid, tab.citedText]) // re-run when citation changes so we jump to the right page

  // --- When tab becomes active, apply deferred scroll ---
  useEffect(() => {
    if (!isActive || !pendingScrollRef.current) return
    pendingScrollRef.current = false
    const targetPage = pendingScrollTargetRef.current
    pendingScrollTargetRef.current = undefined
    requestAnimationFrame(() => applyAndScroll(tab.citedText, targetPage))
  }, [isActive, tab.citedText, applyAndScroll])

  // --- Scroll handler: sticky section + edge loading ---
  useEffect(() => {
    const scrollEl = scrollContainerRef.current
    const displayEl = displayRef.current
    if (!scrollEl || !displayEl) return

    const onScroll = () => {
      // Sticky section tracking
      const scrollTop = scrollEl.scrollTop
      const headings = Array.from(displayEl.querySelectorAll<HTMLElement>('h2'))
      let current: string | null = null
      for (const h of headings) {
        if (h.offsetTop <= scrollTop + 2) current = h.textContent?.trim() ?? null
      }
      setStickySection(current)

      const { start, end } = loadedRangeRef.current
      const total = totalPagesRef.current
      if (!end || !total) return

      // Near bottom → append next page, evict from top if over limit
      if (!loadingEdgeRef.current.bottom && end < total &&
          scrollEl.scrollHeight - scrollTop - scrollEl.clientHeight < 300) {
        loadingEdgeRef.current.bottom = true
        setLoadingBottom(true)
        getPageHtml(end + 1)
          .then(html => {
            if (!displayRef.current) return
            displayRef.current.insertAdjacentHTML('beforeend', html)
            const newEnd = end + 1
            let newStart = start
            if (newEnd - newStart + 1 > MAX_DOM_PAGES) {
              const evict = displayRef.current.querySelector<HTMLElement>(`[data-page="${newStart}"]`)
              if (evict) evict.remove()
              newStart++
            }
            loadedRangeRef.current = { start: newStart, end: newEnd }
          })
          .catch(console.error)
          .finally(() => { loadingEdgeRef.current.bottom = false; setLoadingBottom(false) })
      }

      // Near top → prepend previous page, evict from bottom if over limit
      if (!loadingEdgeRef.current.top && start > 1 && scrollTop < 300) {
        loadingEdgeRef.current.top = true
        setLoadingTop(true)
        getPageHtml(start - 1)
          .then(html => {
            if (!displayRef.current) return
            const prevHeight = scrollEl.scrollHeight
            displayRef.current.insertAdjacentHTML('afterbegin', html)
            scrollEl.scrollTop += scrollEl.scrollHeight - prevHeight
            const newStart = start - 1
            let newEnd = end
            if (newEnd - newStart + 1 > MAX_DOM_PAGES) {
              const evict = displayRef.current.querySelector<HTMLElement>(`[data-page="${newEnd}"]`)
              if (evict) evict.remove()
              newEnd--
            }
            loadedRangeRef.current = { start: newStart, end: newEnd }
          })
          .catch(console.error)
          .finally(() => { loadingEdgeRef.current.top = false; setLoadingTop(false) })
      }
    }

    scrollEl.addEventListener('scroll', onScroll, { passive: true })
    return () => scrollEl.removeEventListener('scroll', onScroll)
  }, [getPageHtml])

  useLayoutEffect(() => {
    if (titleBarRef.current) setTitleBarHeight(titleBarRef.current.offsetHeight)
  }, [meta])

  return (
    <div className="flex flex-col flex-1 min-h-0 w-full h-full">
      {(tab.citedText || notice) && (
        <div className="px-3 py-2.5 flex-shrink-0">
          {tab.citedText && (
            <>
              <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1.5">Cited text</div>
              <div className="bg-gray-50 border border-gray-200 rounded-xl px-2.5 py-2 text-[13px] leading-relaxed text-gray-600 overflow-y-auto max-h-24">
                {tab.citedText}
              </div>
            </>
          )}
          {notice && (
            <div className="mt-2 flex items-center gap-1.5 px-2.5 py-1.5 bg-amber-50 border border-amber-200 rounded-lg text-[11px] text-amber-700">
              {notice}
            </div>
          )}
        </div>
      )}

      <div className="relative flex-1 min-h-0 px-3 pb-3 pt-1">
        <div className="relative h-full rounded-xl border border-gray-200 overflow-hidden">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Loader2 className="w-4 h-4 animate-spin" />Loading document…
              </div>
            </div>
          )}
          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
              <p className="text-sm text-gray-500 text-center px-6">{error}</p>
            </div>
          )}

          <div ref={scrollContainerRef} className="absolute inset-0 overflow-auto bg-white">
            {meta && (
              <div ref={titleBarRef} className="sticky top-0 z-20 bg-white border-b border-gray-100 px-4 py-3">
                <div className="text-[11px] font-medium text-brand/60 tracking-wide mb-1 truncate">{meta.legal_ref}</div>
                <div className="text-lg font-bold text-brand leading-tight truncate">{meta.title}</div>
              </div>
            )}
            <div
              className="sticky z-10 bg-white px-4 overflow-hidden"
              style={{
                top: titleBarHeight,
                height: stickySection ? '26px' : '0',
                borderBottom: stickySection ? '1px solid #BFDDFF' : 'none',
                transition: 'height 0.15s ease',
              }}
            >
              <span className="text-[12px] font-bold text-brand uppercase tracking-[0.06em] truncate leading-[26px] block">
                {stickySection}
              </span>
            </div>

            {/* Top lazy-load spinner */}
            {loadingTop && (
              <div className="flex justify-center py-3">
                <Loader2 className="w-4 h-4 animate-spin text-gray-300" />
              </div>
            )}

            <div className="px-4 pt-2 pb-4">
              <div ref={displayRef} className="regulation-content text-[13.5px] leading-relaxed text-gray-900" />
            </div>

            {/* Bottom lazy-load spinner */}
            {loadingBottom && (
              <div className="flex justify-center py-3">
                <Loader2 className="w-4 h-4 animate-spin text-gray-300" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
})

/**
 * Highlight citedText inside a rendered regulation markdown container.
 * Uses the same alphanumeric-only matching algorithm as highlightPdfQuote —
 * strips punctuation/spaces, finds match, maps back to original char positions,
 * splits text nodes at boundaries, wraps in <mark class="reg-highlight">.
 */
function highlightRegulationText(container: HTMLElement, citedText: string): void {
  // Clear previous highlights
  container.querySelectorAll('.reg-highlight').forEach(el => {
    const parent = el.parentNode!
    while (el.firstChild) parent.insertBefore(el.firstChild, el)
    parent.removeChild(el)
  })

  if (!citedText || citedText.length < 6) return

  function onlyLetters(s: string) { return s.replace(/[^\p{L}\p{N}]/gu, '').toLowerCase() }
  function strippedPosToOriginal(orig: string, strippedPos: number): number {
    let count = 0
    for (let i = 0; i < orig.length; i++) {
      if (/[\p{L}\p{N}]/u.test(orig[i])) {
        if (count === strippedPos) return i
        count++
      }
    }
    return orig.length
  }

  const segments = citedText.split(/\.{3}|…/).map(s => onlyLetters(s)).filter(s => s.length > 0)
  if (!segments.length) return

  // Collect text nodes (skip those inside script/style)
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null)
  const textNodes: Text[] = []
  let n: Node | null
  while ((n = walker.nextNode())) {
    const parent = (n as Text).parentElement
    if (parent && ['SCRIPT', 'STYLE'].includes(parent.tagName)) continue
    textNodes.push(n as Text)
  }

  const divOrigTexts = textNodes.map(tn => tn.textContent ?? '')
  const divStripped = divOrigTexts.map(onlyLetters)
  const divStartInFull: number[] = []
  let fullStripped = ''
  for (const s of divStripped) { divStartInFull.push(fullStripped.length); fullStripped += s }

  const ranges = new Map<number, [number, number]>()
  for (const segment of segments) {
    const match = locateInStripped(segment, fullStripped)
    if (!match) continue
    const { matchStart, matchEnd } = match

    for (let i = 0; i < textNodes.length; i++) {
      const ds = divStartInFull[i], de = ds + divStripped[i].length
      if (matchStart >= de || matchEnd <= ds) continue
      if (!divStripped[i]) continue
      const segStart = Math.max(0, matchStart - ds)
      const segEnd = Math.min(divStripped[i].length, matchEnd - ds)
      // Union with existing range for this node (multiple segments can overlap)
      const existing = ranges.get(i)
      ranges.set(i, existing
        ? [Math.min(existing[0], segStart), Math.max(existing[1], segEnd)]
        : [segStart, segEnd])
    }
  }

  if (!ranges.size) return

  for (const [idx, [strStart, strEnd]] of ranges) {
    const tn = textNodes[idx]
    const orig = divOrigTexts[idx]
    // Skip if original text is only whitespace
    if (!orig.trim()) continue
    const origStart = strippedPosToOriginal(orig, strStart)
    const origEnd = strippedPosToOriginal(orig, strEnd)
    if (origStart >= origEnd) continue
    const parent = tn.parentNode!
    const before = document.createTextNode(orig.slice(0, origStart))
    const mark = document.createElement('mark')
    mark.className = 'reg-highlight'
    mark.textContent = orig.slice(origStart, origEnd)
    const after = document.createTextNode(orig.slice(origEnd))
    parent.insertBefore(before, tn)
    parent.insertBefore(mark, tn)
    parent.insertBefore(after, tn)
    parent.removeChild(tn)
  }
}
